import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/secure/v1/service.pb';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { FaxMediaHooks, FaxMediaQueries } from '@frontend/api-fax-media';
import { Page } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { SelectImageModal } from '@frontend/select-image-modal';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  RTEditor,
  useDebouncedFn,
  Text,
  Button,
  useFormField,
  useModalControl,
  useAlert,
  Modal,
} from '@frontend/design-system';
import { LeaveConfirmationModal } from '../components/draft/LeaveConfirmationModal';
import { EmptyStateGraphic } from '../components/empty-state';
import { FaxCreationStepper } from '../components/stepper/fax-creation-stepper';
import { AssignLocationAccessModal } from '../components/templates/assign-location-access-modal';
import { SaveTemplateModal } from '../components/templates/save-template-modal';
import { dynamicFields } from '../constants';

const fieldValues = Object.values(dynamicFields);

export const FaxCoversheetEditor = ({ draftId }: { draftId: string }) => {
  const alert = useAlert();
  const search = useSearch({ from: '/fax/drafts/$draftId/cover', shouldThrow: false });
  const { selectedLocationId } = search ?? {};
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const faxTemplatesFlag = getFeatureFlagValue('fax-templates');
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const [currHTML, setCurrHTML] = useState<string>();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const [coverSheetDisabled, setCoverSheetDisabled] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const invalidateFaxTemplateList = FaxMediaHooks.useInvalidateFaxTemplatesList();
  const {
    data: draft,
    refetch,
    isLoading,
  } = FaxDraftQueries.useGetDraft(locationId, draftId, {
    enabled: !!draftId,
    keepPreviousData: true,
    refetchOnMount: true,
    retry: 2,
  });
  const { modalProps: saveTemplateModalProps, triggerProps: saveTemplateTriggerProps } = useModalControl();
  const { modalProps: addImageModalProps, triggerProps: addImageTriggerProps } = useModalControl();
  const shouldContinueAfterSaveRef = useRef(false);
  const nameFieldProps = useFormField({ type: 'text', placeholder: t('New Template Title'), value: '' }, []);

  const [locationsWithAccess, setLocationsWithAccess] = useState<string[]>(locationId ? [locationId] : []);

  const { modalProps: locationAccessModalProps, triggerProps: locationAccessTriggerProps } = useModalControl();

  const invalidate = FaxDraftQueries.useInvalidateDraft();
  const { mutateAsync: setHTML } = FaxDraftQueries.useUpdateCoversheetHtml({});
  const { mutateAsync: removeCoversheetFile } = FaxDraftQueries.useUnsetCoversheetUploadedFile({});
  const { mutateAsync: removeHtmlCoversheet } = FaxDraftQueries.useUnsetCoversheetHtml({});
  const { mutateAsync: renderCoversheet, isLoading: isRendering } = FaxDraftQueries.useRenderCoversheet({});
  const [isSaving, setIsSaving] = useState(false);
  const isManuallySavingHTMLRef = useRef(false);

  const { data: template, isLoading: isLoadingDraft } = FaxMediaQueries.useGetCoversheetTemplate(
    draft?.coversheet?.templateId ?? '',
    locationId ?? '',
    {
      enabled: !!draft?.coversheet?.templateId,
      onSuccess: (data) => {
        setCurrHTML(data.html);
      },
    }
  );

  useEffect(() => {
    if (template?.html) {
      setCurrHTML(template.html);
    }
  }, [template?.html]);

  const { mutateAsync: upsertCoversheetTemplate } = FaxMediaQueries.useUpsertCoversheetTemplate({
    onSuccess: () => {
      invalidateFaxTemplateList();
      alert.success({
        message: t('Template updated successfully.'),
      });
    },
  });

  const { mutateAsync: renderCoversheetTemplate } = FaxMediaQueries.useRenderCoversheet({});

  const saveCoversheetData = async ({ forceApplyTemplate = false }: { forceApplyTemplate?: boolean } = {}) => {
    if (coverSheetDisabled || !currHTML) return;

    isManuallySavingHTMLRef.current = true;
    await setHTML({
      locationId,
      draftId: draftId,
      coversheetHtml: currHTML,
    });
    isManuallySavingHTMLRef.current = false;

    await renderCoversheet({
      locationId,
      draftId: draftId,
      contactId: draft?.recipients && draft?.recipients.length > 0 ? draft?.recipients?.[0].contactId : '',
    });

    if (forceApplyTemplate && template?.html && currHTML !== template?.html) {
      const currentTime = new Date().toISOString();

      const renderedCoversheetTemplate = await renderCoversheetTemplate({
        locationId,
        html: currHTML,
        populateDynamicFields: false,
      });

      await upsertCoversheetTemplate({
        locationId,
        coversheetTemplate: {
          id: draft?.coversheet?.templateId,
          name: template?.name ?? '',
          locationIds: template?.locationIds ?? [],
          creatorLocationId: template?.creatorLocationId,
          html: currHTML,
          createdAt: template?.createdAt ?? currentTime,
          updatedAt: currentTime,
          userId: template?.userId ?? '',
          attributes: renderedCoversheetTemplate?.attributes || {
            pages: 1,
            size: 5,
          },
        },
      });
    }
    invalidate(draftId, locationId, selectedLocationIds);
  };

  const saveAndContinue = async () => {
    if (coverSheetDisabled || !currHTML) {
      return navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    }

    try {
      setIsSaving(true);
      await saveCoversheetData();

      navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    } catch (e) {
      alert.error({
        message: t('Error updating template.'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const applyChangesAndContinue = async () => {
    if (coverSheetDisabled || !currHTML) {
      return navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    }

    try {
      setIsSaving(true);
      await saveCoversheetData({ forceApplyTemplate: true });

      navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    } catch (e) {
      alert.error({
        message: t('Error updating template.'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const autosave = useDebouncedFn((html: string) => {
    if (coverSheetDisabled || !draft?.id || isManuallySavingHTMLRef.current) {
      return;
    }
    setHTML({
      locationId,
      draftId: draft.id,
      coversheetHtml: html,
    });
  }, 2000);

  const onEditorChange = (html: string) => {
    setCurrHTML(html);
    autosave(html);
  };

  const back = () =>
    navigate({ to: '/fax/drafts/$draftId/begin', params: { draftId }, search: { selectedLocationId } });

  const hasHTMLCoversheet = !!draft?.coversheet?.html;
  const hasUploadedCoversheet = !!draft?.uploadedCoversheet?.smallThumbnailId;

  const disableCoversheet = () => {
    setCoverSheetDisabled(true);
    removeHtmlCoversheet({ draftId, locationId }).then(() => {
      refetch();
    });
  };

  const enableCoversheet = () => {
    setCoverSheetDisabled(false);
    removeCoversheetFile({ draftId, locationId }).finally(() => {
      back();
    });
  };

  const onStepClick = (stepIndex: number) => {
    const step = stepIndex + 1;
    if (step === 1) {
      return back();
    }
    return;
  };

  if (!draft || isLoading) {
    return (
      <Page
        showBackBtn
        title={t('Draft Not Found')}
        customBackFn={() => {
          navigate({ to: '/fax/$filter', params: { filter: 'drafts' } });
        }}
        loading={isLoading || isLoadingDraft}
      >
        <EmptyStateGraphic />
      </Page>
    );
  }

  return (
    <section
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <header
        css={css`
          border-bottom: 1px solid ${theme.colors.neutral20};
          padding: ${isMobile ? theme.spacing(2) : theme.spacing(3)} 0;
        `}
      >
        <FaxCreationStepper
          currentStep={2}
          onStepClick={onStepClick}
          onSkip={hasHTMLCoversheet ? disableCoversheet : undefined}
          onContinue={faxTemplatesFlag ? saveAndContinue : undefined}
          continueLoading={isRendering || isSaving}
          onApplyChangesToTemplate={
            faxTemplatesFlag && !!draft?.coversheet?.templateId ? applyChangesAndContinue : undefined
          }
          onSaveAsNewTemplate={
            faxTemplatesFlag && hasHTMLCoversheet
              ? () => {
                  shouldContinueAfterSaveRef.current = true;
                  if (locationId && selectedLocationIds.length > 1) {
                    locationAccessTriggerProps.onClick();
                  } else {
                    saveTemplateTriggerProps.onClick();
                  }
                }
              : undefined
          }
        />
      </header>
      <div
        className='editor'
        css={css`
          padding: ${isMobile ? '0' : theme.spacing(2)};
          background: ${theme.colors.neutral5};
          height: 100%;
          overflow: scroll;
          .editor-container {
            margin-top: 0 !important;
            background: transparent !important;
          }
          .editor-inner {
            background: transparent !important;
            height: 100%;
          }
          .editor-scroller {
            height: 100%;
            overflow: hidden;
          }
          .toolbar-container {
            justify-content: center;
            position: sticky;
            top: 0;
          }

          .editor-input {
            height: 100%;
          }
        `}
      >
        {hasHTMLCoversheet ? (
          <RTEditor uniqueId={`draft-${draft.id}`} initialHTML={draft.coversheet?.html} onHTMLChange={onEditorChange}>
            <RTEditor.Toolbar>
              <RTEditor.Toolbar.History />
              <RTEditor.Toolbar.FontPicker />
              <RTEditor.Toolbar.FontSize />
              <RTEditor.Toolbar.BlockType />
              <RTEditor.Toolbar.Bold />
              <RTEditor.Toolbar.Italic />
              <RTEditor.Toolbar.Underline />
              <RTEditor.Toolbar.Image
                onClick={() => {
                  addImageTriggerProps.onClick();
                }}
                src={selectedImage}
                setSrc={setSelectedImage}
              />
              <RTEditor.Toolbar.FontColor />
              <RTEditor.Toolbar.ElementFormat />
              <RTEditor.Toolbar.DynamicField fields={fieldValues} />
            </RTEditor.Toolbar>

            <div
              className='fax-page'
              css={css`
                aspect-ratio: 8.5/11;
                width: ${isMobile ? '100%' : '612px'}; // 8.5in
                height: 792px; // 11in
                overflow-y: scroll;
                background: ${theme.colors.white};
                margin: auto;
                margin-top: ${theme.spacing(2)};
                margin-bottom: ${theme.spacing(2)};
                border-radius: ${isMobile ? '0' : theme.borderRadius.medium};
                border: 1px solid ${theme.colors.neutral20};
                box-shadow: ${theme.shadows.light};
                box-sizing: border-box;
                padding: ${theme.spacing(2)};
              `}
            >
              <RTEditor.Editor />
            </div>
          </RTEditor>
        ) : hasUploadedCoversheet ? (
          <div
            css={css`
              text-align: center;
              margin-top: ${theme.spacing(4)};
            `}
          >
            <Text
              textAlign='center'
              color='subdued'
              size='large'
              css={css`
                margin-bottom: ${theme.spacing(2)};
              `}
            >
              {t('This Fax has an uploaded cover sheet attached')}
            </Text>
            <Button
              css={css`
                background: ${theme.colors.white};
                width: auto;
              `}
              onClick={() => back()}
              variant='secondary'
            >
              {t('Change Cover Sheet')}
            </Button>
          </div>
        ) : (
          <div
            css={css`
              text-align: center;
              margin-top: ${theme.spacing(4)};
            `}
          >
            <Text
              textAlign='center'
              color='subdued'
              size='large'
              css={css`
                margin-bottom: ${theme.spacing(2)};
              `}
            >
              {t('Cover Sheet Disabled for this Fax')}
            </Text>
            <Button
              css={css`
                background: ${theme.colors.white};
                width: auto;
              `}
              onClick={enableCoversheet}
              variant='secondary'
            >
              {t('Enable Cover Sheet')}
            </Button>
          </div>
        )}
      </div>
      {locationId && selectedLocationIds.length > 1 && (
        <AssignLocationAccessModal
          modalProps={locationAccessModalProps}
          onSave={(locations) => {
            setLocationsWithAccess(locations);
            locationAccessModalProps.onClose();
            saveTemplateTriggerProps.onClick();
          }}
          locationsWithAccess={locationsWithAccess}
          mode='progression'
        />
      )}
      {faxTemplatesFlag ? (
        <SaveTemplateModal
          fieldProps={nameFieldProps}
          modalProps={saveTemplateModalProps}
          locationsWithAccess={locationsWithAccess}
          locationId={locationId}
          html={currHTML}
          onSaveSuccess={() => {
            if (shouldContinueAfterSaveRef.current) {
              shouldContinueAfterSaveRef.current = false;
              navigate({
                to: '/fax/drafts/$draftId/upload',
                params: { draftId },
                search: { selectedLocationId },
              });
            }
          }}
        />
      ) : null}
      <LeaveConfirmationModal
        draftId={draftId}
        shouldBlock={true}
        locationId={locationId}
        onSaveAndContinue={saveCoversheetData}
      />
      <Modal {...addImageModalProps} minWidth={700}>
        <SelectImageModal
          locationIds={[locationId]}
          modalProps={addImageModalProps}
          onSelect={(media?: Media) => {
            if (media?.url) {
              setSelectedImage(media?.url);
            }
            addImageModalProps.onClose();
          }}
          showLocationFilter
        />
      </Modal>
    </section>
  );
};
